import type { WidgetConfig } from "@/types/config";

/**
 * Button component - handles button styling based on configuration
 */
export class Button {
  constructor(
    private button: HTMLButtonElement,
    private config: WidgetConfig
  ) {}

  /**
   * Apply Ant Design button classes
   */
  public applyStyles(): void {
    // Base button class
    this.button.classList.add("caredove-btn");

    // Button type (primary, secondary, link)
    const buttonType = this.config.buttonType || "primary";
    switch (buttonType) {
      case "primary":
        this.button.classList.add("caredove-btn--primary");
        break;
      case "secondary":
        this.button.classList.add("caredove-btn--secondary");
        break;
      case "link":
        this.button.classList.add("caredove-btn--link");
        break;
    }

    // Button size (small, medium, large)
    const buttonSize = this.config.buttonSize || "large";
    switch (buttonSize) {
      case "small":
        this.button.classList.add("caredove-btn--sm");
        break;
      case "large":
        this.button.classList.add("caredove-btn--lg");
        break;
      // medium is default, no extra class needed
    }

    // Apply theme colors via CSS variables
    this.applyThemeColors();

    // Apply border radius
    if (this.config.borderRadius) {
      this.button.style.setProperty(
        "--caredove-border-radius",
        this.config.borderRadius
      );
    }
  }

  /**
   * Apply theme colors via CSS custom properties
   */
  private applyThemeColors(): void {
    if (this.config.buttonColor) {
      this.button.style.setProperty(
        "--caredove-button-color",
        this.config.buttonColor
      );
    }

    if (this.config.buttonHoverColor) {
      this.button.style.setProperty(
        "--caredove-button-hover-color",
        this.config.buttonHoverColor
      );
    }

    if (this.config.buttonTextColor) {
      this.button.style.setProperty(
        "--caredove-button-text-color",
        this.config.buttonTextColor
      );
    }

    if (this.config.buttonTextHoverColor) {
      this.button.style.setProperty(
        "--caredove-button-text-hover-color",
        this.config.buttonTextHoverColor
      );
    }

    if (this.config.buttonBorderColor) {
      this.button.style.setProperty(
        "--caredove-button-border-color",
        this.config.buttonBorderColor
      );
    }

    if (this.config.buttonBorderHoverColor) {
      this.button.style.setProperty(
        "--caredove-button-border-hover-color",
        this.config.buttonBorderHoverColor
      );
    }

    if (this.config.borderColor) {
      this.button.style.setProperty(
        "--caredove-border-color",
        this.config.borderColor
      );
    }
  }

  /**
   * Remove all applied styles
   */
  public removeStyles(): void {
    this.button.classList.remove(
      "caredove-btn",
      "caredove-btn--primary",
      "caredove-btn--secondary",
      "caredove-btn--link",
      "caredove-btn--link-styled",
      "caredove-btn--sm",
      "caredove-btn--lg"
    );

    // Remove CSS variables
    this.button.style.removeProperty("--caredove-button-color");
    this.button.style.removeProperty("--caredove-button-hover-color");
    this.button.style.removeProperty("--caredove-button-text-color");
    this.button.style.removeProperty("--caredove-button-text-hover-color");
    this.button.style.removeProperty("--caredove-button-border-color");
    this.button.style.removeProperty("--caredove-button-border-hover-color");
    this.button.style.removeProperty("--caredove-border-color");
    this.button.style.removeProperty("--caredove-border-radius");
  }
}
